import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, SwitcherButtonProps } from "components/buttons/Button";
import SwitcherLogo from "assets/SwitcherPlayerIconGradient.svg?react";
import styles from "./index.module.scss";
import { useNavigate, useRouteError } from "react-router-dom";
import { trackEvent } from "helpers/analyticsHelpers";
import { useSupportSystem } from "hooks/useSupportSystem";
import { ParsedLine, parseLine } from "helpers/url";

const environment = import.meta.env.MODE;
const projectDirectory = import.meta.env.VITE_PROJECT_DIRECTORY;

export interface ErrorPageProps {
    /**
     * Variant of the error page to display.
     * - "default": Default error page with options to go back home or reload.
     * - "not-found": Specific error page for 404 not found errors.
     */
    variant?: "default" | "not-found";
}

export const ErrorPage = ({ variant = "default" }: ErrorPageProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { isSupportAvailable, supportSystemCommand } = useSupportSystem();

    const error: any = useRouteError();

    const header = useMemo<string>(() => {
        switch (variant) {
            case "not-found":
                return t("error:not-found:header");
            default:
                return t("error:default:header");
        }
    }, [t, variant]);

    const handleBackToHome = useCallback(() => {
        navigate("/");
    }, [navigate]);

    const handleReentry = useCallback(() => {
        window.location.reload();
    }, []);

    const handleContactSupport = useCallback(() => {
        () => {
            if (isSupportAvailable) {
                trackEvent("Opened Support Widget", {
                    source: "error-page"
                });
                supportSystemCommand("show");
            } else {
                window.open("mailto:support@switcherstudio.com");
            }
        };
    }, [isSupportAvailable, supportSystemCommand]);

    const actions = useMemo<SwitcherButtonProps[]>(() => {
        switch (variant) {
            case "not-found":
                return [
                    {
                        children: t("error:action:back-to-home"),
                        onClick: handleBackToHome
                    }
                ];
            default:
                return [
                    {
                        children: t("error:action:back-to-home"),
                        onClick: handleBackToHome
                    },
                    {
                        children: t("error:action:reentry"),
                        type: "outline",
                        onClick: handleReentry
                    },
                    {
                        children: t("error:action:contact-support"),
                        type: "outline",
                        onClick: handleContactSupport,
                        hide: !isSupportAvailable
                    }
                ];
        }
    }, [
        handleBackToHome,
        handleContactSupport,
        handleReentry,
        isSupportAvailable,
        t,
        variant
    ]);

    const stack = useMemo<ParsedLine[]>(
        () =>
            (error?.stack ? error.stack.split("\n").slice(0, 15) : []).map(
                parseLine
            ),
        [error]
    );

    if (environment === "development" && variant === "default") {
        return (
            <section className={styles["error-page"]}>
                <div className={styles["develop-page-container"]}>
                    <div className={styles["error-header"]}>
                        <SwitcherLogo />
                        <div>
                            <h1>{header}</h1>
                            <h4>{error.message}</h4>
                        </div>
                    </div>

                    <code className={styles["error-stack"]}>
                        {stack.map(
                            ({ component, url, line, column }, index) => (
                                <span key={index}>
                                    <strong>{component}</strong>
                                    {"   "}@{"   "}
                                    <a
                                        href={`vscode://file/${projectDirectory}${url.pathname}:${line}:${column}`}
                                    >
                                        {url.pathname}:{line}:{column}
                                    </a>
                                    <br />
                                </span>
                            )
                        )}
                    </code>
                </div>
            </section>
        );
    }

    return (
        <section className={styles["error-page"]}>
            <SwitcherLogo />
            <div className={styles["error-page-container"]}>
                <h1>{header}</h1>
                <h4>{t("error:subheader")}</h4>
                <p>{t("error:description")}</p>
                <div className={styles["actions-container"]}>
                    {actions.map((props, index) => (
                        <Button key={index} {...props} />
                    ))}
                </div>
            </div>
        </section>
    );
};
